// CreateTemplateModal.jsx — Phase 2 (2026-09)
// Compact "create a new WhatsApp template" form invoked from inside the
// unified TemplatePicker (surveys + campaigns). Submits to the SAME Meta
// approval flow used by صندوق القوالب: POST /whatsapp/templates/submit →
// the template lands in the box with status "بانتظار اعتماد Meta". No new
// approval mechanism — reuses the existing one.
import React, { useMemo, useState } from "react";
import { api } from "./lib/api";
import { toast } from "sonner";
import HeaderImageField, { uploadHeaderImage } from "./HeaderImageField";
import { zVar } from "./lib/zIndex";

const extractVars = (t) => {
  const s = new Set(); const re = /\{\{(\d+)\}\}/g; let m;
  while ((m = re.exec(t || "")) !== null) s.add(m[1]);
  return Array.from(s).sort((a, b) => Number(a) - Number(b));
};

export default function CreateTemplateModal({ onClose, onSubmitted }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState("ar");
  const [bodyText, setBodyText] = useState("");
  const [samples, setSamples] = useState({});
  const [busy, setBusy] = useState(false);
  // رأس صورة (اختياري) — التحقق والرفع في المكوّن المشترك HeaderImageField.
  const [headerImage, setHeaderImage] = useState(null);

  const vars = useMemo(() => extractVars(bodyText), [bodyText]);
  const cleanName = name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");

  const submit = async () => {
    if (!cleanName || !bodyText.trim()) {
      toast.error("الاسم والنص مطلوبان");
      return;
    }
    if (vars.some((n) => !(samples[n] || "").trim())) {
      toast.error("أدخل قيمة تجريبية لكل متغيّر (يطلبها Meta)");
      return;
    }
    setBusy(true);
    try {
      // (رأس صورة) — ارفع الصورة أولًا للحصول على المقبض (المصدر المشترك).
      let header_handle = null;
      if (headerImage) {
        try {
          header_handle = await uploadHeaderImage(headerImage);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "تعذّر رفع صورة الرأس إلى Meta");
          setBusy(false);
          return;
        }
      }
      await api.post("/whatsapp/templates/submit", {
        name: cleanName, category, language, body: bodyText.trim(),
        sample_values: vars.map((n) => samples[n]),
        header_handle,
      });
      toast.success("📨 أُرسل القالب لاعتماد Meta — بانتظار الاعتماد (دقائق حتى 24 ساعة). سيظهر بالقائمة فور اعتماده.");
      if (typeof onSubmitted === "function") onSubmitted(cleanName);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الإرسال إلى Meta");
    } finally { setBusy(false); }
  };

  const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "#57534E", marginBottom: 4 };
  const inp = { width: "100%", padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(61,42,28,0.18)", fontSize: 13, fontFamily: "inherit", background: "#fff" };

  return (
    <div data-testid="create-template-modal" dir="rtl" onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: zVar('extra_9999'),
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, padding: 20, width: "100%", maxWidth: 560,
        maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#3D2A1C" }}>＋ إنشاء قالب جديد (يُرسَل لاعتماد Meta)</div>
          <button data-testid="create-template-close" onClick={onClose} className="btn btn-ghost" style={{ fontSize: 18 }}>✕</button>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={lbl}>اسم القالب (إنجليزي، أحرف صغيرة وأرقام و_)</label>
            <input data-testid="create-template-name" style={inp} value={name}
              onChange={(e) => setName(e.target.value)} placeholder="product_feedback_v1" />
            {name && <div style={{ fontSize: 11, color: "#78716C", marginTop: 3 }}>سيُحفظ باسم: <b style={{ fontFamily: "monospace" }}>{cleanName}</b></div>}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>الفئة</label>
              <select data-testid="create-template-category" style={inp} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="UTILITY">UTILITY — رسائل خدمية</option>
                <option value="MARKETING">MARKETING — تسويقية</option>
              </select>
            </div>
            <div style={{ width: 120 }}>
              <label style={lbl}>اللغة</label>
              <select data-testid="create-template-language" style={inp} value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="ar">ar</option>
                <option value="en">en</option>
              </select>
            </div>
          </div>
          <HeaderImageField file={headerImage} onChange={setHeaderImage} testidPrefix="create-template-header" />
          <div>
            <label style={lbl}>نص القالب (استخدم {"{{1}}"} للمتغيرات مثل الرابط)</label>
            <textarea data-testid="create-template-body" style={{ ...inp, minHeight: 110, resize: "vertical", lineHeight: 1.8 }}
              value={bodyText} onChange={(e) => setBodyText(e.target.value)}
              placeholder="شكراً لزيارتك ثواني! شاركنا رأيك بالمنتج عبر هذا الرابط: {{1}}" />
          </div>
          {vars.length > 0 && (
            <div style={{ display: "grid", gap: 8, padding: 10, background: "#F7F2EC", borderRadius: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E" }}>قيم تجريبية للمتغيرات (تطلبها Meta للاعتماد):</div>
              {vars.map((n) => (
                <label key={n} style={{ display: "grid", gap: 3 }}>
                  <span style={{ fontSize: 11, color: "#57534E" }}>{`{{${n}}}`}</span>
                  <input data-testid={`create-template-sample-${n}`} style={inp}
                    value={samples[n] || ""} onChange={(e) => setSamples((s) => ({ ...s, [n]: e.target.value }))} />
                </label>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button className="btn" onClick={onClose} disabled={busy} style={{ minHeight: 42 }}>إلغاء</button>
            <button data-testid="create-template-submit" className="btn btn-primary" onClick={submit} disabled={busy}
              style={{ minHeight: 42, fontWeight: 800, background: "#128C4B", borderColor: "#128C4B" }}>
              {busy ? "⏳ جارٍ الإرسال…" : "📨 أرسل لاعتماد Meta"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
